// The navigation trail (ADR 0030, DESIGN.md "Navigation and breadcrumbs"), shared by the desktop
// app and the console. Every history entry carries the levels above its page: the pages the
// person came through, each with its name, address, history index and scroll. A move to another
// page appends the page on screen; a sidebar click starts over; re-entering a page already in the
// trail cuts the trail back to it. The crumbs, Back and the sidebar highlight all read it.
// Router agnostic: each app wires its router's history (`install`) and says, per route, what the
// page is called and what its natural parents are (used when there is no trail).

import { useEffect, useSyncExternalStore } from "react";
import {
  captureScroll,
  freshState,
  historyIndex,
  isFreshState,
  restoreScroll,
  type ScrollSnap,
} from "./navOrigin";
import { type BlockableHistory, blockBackWhileUnsaved, leaveIfAllowed } from "./unsaved";

/** One level of a trail: a page above the one on screen. */
export interface TrailLevel {
  /** Its name as the crumb says it ("Lists", "Q4 cold list", "Calls & Dispositions"). */
  label: string;
  /** Path, search and hash, so its filters, open tab and selected item come back. */
  href: string;
  /** The router's history index of that page's entry, when it was the page on screen. */
  index?: number | null;
  /** Where its scrolling parts were. */
  scroll?: ScrollSnap[];
}

/** The history state key the trail rides under. */
export const TRAIL_STATE_KEY = "vdTrail";

/** The longest a label is kept, and the most levels a trail keeps. */
const MAX_LABEL = 80;
const MAX_LEVELS = 12;

interface TrailState {
  owner?: string;
  levels: TrailLevel[];
}

export const pathOf = (href: string) => href.split(/[?#]/)[0] ?? href;

/** The search parameters of an address. */
function searchOf(href: string): URLSearchParams {
  const q = href.split("#")[0]?.split("?")[1] ?? "";
  return new URLSearchParams(q);
}

/**
 * How deep an address sits inside its page: how many of the page's level keys it sets
 * (`levelKeys: ["rep"]`: `?dashboard=x` is 0, `?dashboard=x&rep=y` is 1).
 */
export function levelDepth(href: string, keys: readonly string[]): number {
  const q = searchOf(href);
  return keys.filter((k) => (q.get(k) ?? "") !== "").length;
}

/**
 * The trail of a move that stays on the page on screen, for a page whose declaration names
 * level keys (a level inside the same page, as a rep opened on a dashboard):
 * - deeper (more level keys set): the page on screen becomes a level, `self` appended to the
 *   levels it inherits;
 * - shallower (back up to the dashboard): the trail as it was when the page was entered, cut
 *   before the first level on this page's path;
 * - as deep (another rep, a filter): the trail it had.
 */
export function inPageTrail(
  cur: TrailLevel[],
  inherited: TrailLevel[],
  self: TrailLevel,
  destHref: string,
  keys: readonly string[],
): TrailLevel[] {
  const from = levelDepth(self.href, keys);
  const to = levelDepth(destHref, keys);
  if (to > from) return [...inherited, self];
  if (to < from) {
    const path = pathOf(destHref);
    const at = cur.findIndex((l) => pathOf(l.href) === path);
    return at >= 0 ? cur.slice(0, at) : cur;
  }
  return cur;
}

function readLevel(x: unknown): TrailLevel | null {
  const o = x as Record<string, unknown> | null;
  if (!o || typeof o !== "object") return null;
  if (typeof o.label !== "string" || !o.label.trim()) return null;
  if (typeof o.href !== "string" || !o.href.startsWith("/")) return null;
  const index = typeof o.index === "number" && Number.isInteger(o.index) ? o.index : null;
  const scroll = Array.isArray(o.scroll)
    ? o.scroll.filter(
        (s): s is ScrollSnap =>
          Boolean(s) &&
          typeof (s as ScrollSnap).key === "string" &&
          typeof (s as ScrollSnap).top === "number",
      )
    : [];
  return { label: o.label.slice(0, MAX_LABEL), href: o.href, index, scroll };
}

/** The trail in a history entry's state, or null when there is none or it is malformed. */
export function readTrail(state: unknown): TrailState | null {
  const t = (state as Record<string, unknown> | null | undefined)?.[TRAIL_STATE_KEY] as
    | Record<string, unknown>
    | undefined;
  if (!t || typeof t !== "object" || !Array.isArray(t.levels)) return null;
  const levels = t.levels.map(readLevel);
  if (levels.some((l) => l === null)) return null;
  const owner = typeof t.owner === "string" && t.owner ? { owner: t.owner } : {};
  return { levels: (levels as TrailLevel[]).slice(-MAX_LEVELS), ...owner };
}

/** The state that carries `levels` (none for an empty trail). */
export function trailState(levels: TrailLevel[], owner: string | null): Record<string, unknown> {
  if (levels.length === 0) return {};
  return {
    [TRAIL_STATE_KEY]: {
      levels: levels.slice(-MAX_LEVELS).map((l) => ({ ...l, label: l.label.slice(0, MAX_LABEL) })),
      ...(owner ? { owner } : {}),
    },
  };
}

/**
 * The trail of a page opened from a page whose levels (its crumbs, itself last) are `from`:
 * those levels, cut back before the destination when it is already one of them (re-entering a
 * page never makes a loop).
 */
export function nextTrail(from: TrailLevel[], destHref: string): TrailLevel[] {
  const dest = pathOf(destHref);
  const at = from.findIndex((l) => pathOf(l.href) === dest);
  return at >= 0 ? from.slice(0, at) : from;
}

/**
 * The levels above a page: its trail, or its natural parents when it has none. When one of the
 * page's natural parents is in the trail (a sibling tab of the same record, a list's Focus opened
 * from the list, a call opened over the list it belongs to), the trail is cut after the deepest
 * such parent and the parents below it follow, so the crumbs never show a sibling as a level
 * and never skip the page's own parent.
 */
export function levelsAbove(trail: TrailLevel[] | null, parents: TrailLevel[]): TrailLevel[] {
  if (!trail || trail.length === 0) return parents;
  for (let p = parents.length - 1; p >= 0; p -= 1) {
    const want = pathOf(parents[p]?.href ?? "");
    for (let at = trail.length - 1; at >= 0; at -= 1) {
      if (pathOf(trail[at]?.href ?? "") === want) {
        return [...trail.slice(0, at + 1), ...parents.slice(p + 1)];
      }
    }
  }
  return trail;
}

/** Two lists of levels with the same addresses, in order. */
function sameLevels(a: TrailLevel[], b: TrailLevel[]): boolean {
  return a.length === b.length && a.every((l, i) => l.href === b[i]?.href);
}

/** Most levels shown before the middle ones fold into a "…" menu (the page itself not counted). */
export const MAX_VISIBLE_LEVELS = 3;

/** The levels shown and the ones folded into the middle menu: the first, then the last ones. */
export function foldLevels<T>(
  levels: T[],
  max = MAX_VISIBLE_LEVELS,
): { head: T[]; folded: T[]; tail: T[] } {
  if (levels.length <= max) return { head: levels, folded: [], tail: [] };
  const keep = Math.max(1, max - 1);
  return {
    head: levels.slice(0, 1),
    folded: levels.slice(1, levels.length - keep),
    tail: levels.slice(levels.length - keep),
  };
}

export type TrailReturn =
  /** The level is the entry right before this one: step back (the browser keeps the rest). */
  | { kind: "back" }
  /** Anything else (a reload, a level further up): open its address with the levels above it. */
  | { kind: "go"; href: string };

/** How to return to `level` from the entry at `index`. */
export function trailReturn(level: TrailLevel, index: number | null): TrailReturn {
  if (level.index != null && index !== null && level.index === index - 1) return { kind: "back" };
  return { kind: "go", href: level.href };
}

/** A router as the kit needs it (TanStack Router's, or a stand-in in a test). */
export interface TrailRouter {
  state: { location: { pathname: string; searchStr: string; hash: string } };
  history: { location: { state: unknown }; back: () => void };
  navigate: (o: { href: string; state?: Record<string, unknown> }) => unknown;
  subscribe?: (event: "onResolved", fn: () => void) => () => void;
}

type Write = (path: string, state?: never, ...rest: never[]) => void;

/** The history as `install` wires it (TanStack's browser or memory history). */
export interface TrailHistory {
  location: { pathname: string; search?: string; hash?: string; state: unknown };
  push: Write;
  replace: Write;
  subscribe: (fn: () => void) => () => void;
  block?: BlockableHistory["block"];
}

export interface NavTrailOptions {
  /** The sidebar tab a path belongs to. */
  tabOf: (pathname: string) => string;
  /** The tab's name, the label of a page that never named itself. */
  tabLabel: (tab: string) => string | undefined;
  /** The app's router, or null before it exists (and in tests without one). */
  router: () => TrailRouter | null;
}

/** What a page's inline title names: itself, or the level above it. */
export type InlineTitle = "self" | "parent";

/**
 * The crumbs a page shows: the levels above it and itself, less whatever its inline title
 * already names. `back` is the level the chevron goes to (always the one right above the page).
 */
export function visibleCrumbs(
  above: TrailLevel[],
  inlineTitle: InlineTitle | null,
): { levels: TrailLevel[]; showSelf: boolean; back: number | null } {
  const back = above.length ? above.length - 1 : null;
  if (inlineTitle === "parent") return { levels: above.slice(0, -1), showSelf: false, back };
  return { levels: above, showSelf: inlineTitle !== "self", back };
}

/** What the page on screen says about itself (from its route's crumb declaration). */
export interface PageCrumbInfo {
  /** Its name; null while it loads (the crumb shows a skeleton). */
  label: string | null;
  /** Its natural parents, top first: used when it was opened with no trail. */
  parents: TrailLevel[];
  /** What the page's own title names, when it sits inline after the crumbs (see CrumbDecl). */
  inlineTitle?: InlineTitle;
  /**
   * A tab's own page (Analytics with a dashboard open, Contacts with a contact open): no crumbs
   * when opened from the sidebar, but its parents still lead the trail of pages opened from it.
   */
  topLevel?: boolean;
  /** Search keys that make a level inside the page (see CrumbDecl.levelKeys). */
  levelKeys?: string[];
}

/** The levels the page on screen shows: the ones above it, then itself. */
export interface PageLevels {
  above: TrailLevel[];
  /** The levels a page opened from this one inherits (its parents even on a tab's own page). */
  inherited: TrailLevel[];
  self: { label: string | null; href: string; inlineTitle: InlineTitle | null };
}

/** How long a return keeps trying to put the scroll back while the page loads. */
const RESTORE_MS = 1500;
/** Frames a restored position must hold before the return stops watching it. */
const SETTLE_FRAMES = 6;

export function createNavTrail({ tabOf, tabLabel, router }: NavTrailOptions) {
  // ---------- the entry on screen ----------
  interface Here {
    pathname: string;
    href: string;
    state: unknown;
  }
  let here: Here = { pathname: "", href: "", state: null };
  type Info = { href: string; page: PageCrumbInfo } | null;
  let info: Info = null;
  let owner: string | null = null;
  let snapshot: { here: Here; info: Info; owner: string | null } = { here, info, owner };
  const listeners = new Set<() => void>();
  const notify = () => {
    snapshot = { here, info, owner };
    for (const l of listeners) l();
  };
  const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  };
  const useSnapshot = () =>
    useSyncExternalStore(
      subscribe,
      () => snapshot,
      () => snapshot,
    );

  /** The signed-in account: trails of another account (or from before sign-out) never show. */
  function setOwner(next: string | null) {
    if (next === owner) return;
    owner = next;
    pending = null;
    notify();
  }

  /** The trail of a history state, when it is this account's. */
  function ownTrail(state: unknown): TrailLevel[] | null {
    const t = readTrail(state);
    if (!t) return null;
    return (t.owner ?? null) === owner ? t.levels : null;
  }

  const fallbackLabel = (pathname: string) => tabLabel(tabOf(pathname)) ?? "Back";

  /** The levels of the page on screen, as its crumbs show them. */
  function levelsOf(h: Here, i: Info): PageLevels {
    const page = i && i.href === h.href ? i.page : null;
    const natural = page?.parents ?? [];
    // A tab's own page whose trail is only its natural parents (returned to from a level below
    // it, as a dashboard from its rep) has nothing above it, as from the sidebar.
    const own = ownTrail(h.state);
    const trail = page?.topLevel && own && sameLevels(own, natural) ? null : own;
    const above = trail?.length ? levelsAbove(trail, natural) : page?.topLevel ? [] : natural;
    return {
      above,
      inherited: trail?.length ? above : natural,
      self: {
        label: page ? page.label : null,
        href: h.href,
        inlineTitle: page?.inlineTitle ?? null,
      },
    };
  }

  /**
   * The page on screen names itself and its natural parents (the app's route declarations,
   * resolved in its root layout). Kept until the next page says its own.
   */
  function usePageInfo(href: string, page: PageCrumbInfo) {
    const key = JSON.stringify(page);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `page`.
    useEffect(() => {
      info = { href, page: JSON.parse(key) as PageCrumbInfo };
      notify();
    }, [href, key]);
  }

  /** The address of the entry on screen (path, search and hash), for `usePageInfo`. */
  function useHereHref(): string {
    return useSnapshot().here.href;
  }

  /** The levels for the page on screen (crumbs, Back, the sidebar highlight). */
  function useLevels(): PageLevels {
    const s = useSnapshot();
    return levelsOf(s.here, s.info);
  }

  /** The tab the sidebar highlights: the tab the trail started in, else the page's own. */
  function useActiveTab(): string {
    const { above, self } = useLevels();
    const first = above[0]?.href ?? self.href;
    return tabOf(pathOf(first));
  }

  /** The page on screen as one level of the next page's trail. */
  function selfLevel(r: TrailRouter | null): TrailLevel {
    const { self } = levelsOf(here, info);
    return {
      label: self.label ?? fallbackLabel(here.pathname),
      href: here.href,
      index: historyIndex(r?.history.location.state ?? here.state),
      scroll: typeof document === "undefined" ? [] : captureScroll(document),
    };
  }

  // ---------- returning ----------
  let pending: { path: string; scroll: ScrollSnap[] } | null = null;

  /**
   * Return to one of the levels above the page on screen: one step back when it is the entry
   * before, else open its address with the levels above it. Asks first when an editor holds
   * unsaved changes; resolves false when the person keeps editing.
   */
  function returnTo(r: TrailRouter, levels: TrailLevel[], at: number): Promise<boolean> {
    const level = levels[at];
    if (!level) return Promise.resolve(false);
    return leaveIfAllowed(() => {
      pending = level.scroll?.length ? { path: pathOf(level.href), scroll: level.scroll } : null;
      const step = trailReturn(level, historyIndex(r.history.location.state));
      if (step.kind === "back") r.history.back();
      else {
        // The top level opens fresh (no trail), as the sidebar would.
        const state = at === 0 ? freshState() : trailState(levels.slice(0, at), owner);
        void r.navigate({ href: step.href, state });
      }
    });
  }

  /** Back from the page on screen, to the level above it. False when there is none. */
  function back(r: TrailRouter | null = router()): boolean {
    if (!r) return false;
    const { above } = levelsOf(here, info);
    if (above.length === 0) return false;
    void returnTo(r, above, above.length - 1);
    return true;
  }

  /** Open a level of the page on screen's crumbs (the router from `router()`). */
  function open(levels: TrailLevel[], at: number) {
    const r = router();
    if (r) void returnTo(r, levels, at);
  }

  function restoreLater(p: { scroll: ScrollSnap[] }) {
    const until = performance.now() + RESTORE_MS;
    let frames = 0;
    const tick = () => {
      frames += 1;
      const landed = restoreScroll(p.scroll);
      if ((landed && frames >= SETTLE_FRAMES) || performance.now() > until) return;
      requestAnimationFrame(tick);
    };
    const r = router();
    if (!r?.subscribe) requestAnimationFrame(tick);
    else {
      const off = r.subscribe("onResolved", () => {
        off();
        requestAnimationFrame(tick);
      });
    }
  }

  /**
   * Wire the history once, at startup. Every move writes the trail into the new entry:
   * - a sidebar move (`freshState()`) starts over: no trail;
   * - a move that names its own trail (a return to a level) keeps it;
   * - a move that stays on the same page (a filter, a search, the open record) or replaces the
   *   entry (a redirect) keeps the trail it had, cut back if it re-enters a level;
   * - a move to another page appends the page on screen, cut back if it re-enters a level.
   * The browser's own Back asks first while an editor holds unsaved changes. A return puts the
   * level's scroll back once its page is there. Returns the unsubscribe (tests).
   */
  function install(history: TrailHistory): () => void {
    const withTrail =
      (write: Write, replacing: boolean): Write =>
      (path: string, state?: never, ...rest: never[]) => {
        const s = (state ?? {}) as Record<string, unknown>;
        let next: Record<string, unknown> = s;
        if (isFreshState(s) || readTrail(s)) {
          next = s;
        } else {
          const cur = ownTrail(history.location.state) ?? [];
          const samePage = pathOf(path) === history.location.pathname;
          const keys = info && info.href === here.href ? (info.page.levelKeys ?? []) : [];
          let levels: TrailLevel[];
          if (samePage && !replacing && keys.length > 0) {
            const mine = levelsOf(here, info);
            levels = inPageTrail(cur, mine.inherited, selfLevel(router()), path, keys);
          } else {
            const from =
              replacing || samePage
                ? cur
                : [...levelsOf(here, info).inherited, selfLevel(router())];
            levels = nextTrail(from, path);
          }
          next = { ...s, ...trailState(levels, owner) };
        }
        write(path, next as never, ...rest);
      };
    history.push = withTrail(history.push.bind(history) as Write, false);
    history.replace = withTrail(history.replace.bind(history) as Write, true);
    const unblock = history.block ? blockBackWhileUnsaved(history as BlockableHistory) : () => {};

    const follow = () => {
      const { pathname, search = "", hash = "", state } = history.location;
      here = { pathname, href: `${pathname}${search}${hash}`, state };
      notify();
      const p = pending;
      if (!p || pathname !== p.path) return;
      pending = null;
      restoreLater(p);
    };
    follow();
    const off = history.subscribe(follow);
    return () => {
      off();
      unblock();
    };
  }

  return {
    install,
    setOwner,
    usePageInfo,
    useHereHref,
    useLevels,
    useActiveTab,
    back,
    open,
    returnTo,
  };
}

export type NavTrailKit = ReturnType<typeof createNavTrail>;

/** What a route's page is called and where it sits (resolved for the page on screen). */
export interface CrumbContext {
  params: Record<string, string>;
  search: Record<string, unknown>;
}

/**
 * A route's crumb declaration, in its route definition (`staticData.crumb`). Every route with a
 * page has one; a test fails on a route without it.
 */
export interface CrumbDecl {
  /** The page's name. */
  label?: string;
  /** The page's name when it loads (a list's name, a call's contact); null while it loads. */
  useLabel?: (c: CrumbContext) => string | null | undefined;
  /** The natural parents, top first, used when the page is opened with no trail. [] for a tab. */
  parents?: TrailLevel[] | ((c: CrumbContext) => TrailLevel[]);
  /** As `parents`, when they load (a member's name above their call). */
  useParents?: (c: CrumbContext) => TrailLevel[];
  /**
   * The page's title sits inline after the crumbs and already names a level, so the crumbs
   * leave it out: "self" (the title is the page's own name) or "parent" (the title names the
   * level above, as Focus names its list: "Lists" then the list's name as the title). Back still
   * goes to the level above.
   */
  inlineTitle?: InlineTitle;
  /** A tab's own page with a record open: no crumbs from the sidebar (see PageCrumbInfo). */
  topLevel?: boolean | ((c: CrumbContext) => boolean);
  /**
   * Search keys that open a level inside the page, on the same route: setting one (a rep on a
   * dashboard, `["rep"]`) makes the page as it was a level of the trail, named by its label,
   * and clearing it returns to that level. Other search changes (a filter, the range) stay on
   * the level. The page's label and parents then describe the deeper level.
   */
  levelKeys?: string[];
}

/** Whether a declaration says both what the page is called and where it sits. */
export function crumbDeclComplete(d: CrumbDecl | undefined): boolean {
  return Boolean(d && (d.label || d.useLabel) && (d.parents || d.useParents));
}

const noLabel = () => null;

/** The page info a declaration gives for a page (hooks: call it at a component's top level). */
export function useCrumbDecl(d: CrumbDecl, c: CrumbContext): PageCrumbInfo {
  const useLabel = d.useLabel ?? noLabel;
  const useParents = d.useParents ?? noParents;
  const loaded = useLabel(c);
  const loadedParents = useParents(c);
  const parents = d.useParents
    ? loadedParents
    : typeof d.parents === "function"
      ? d.parents(c)
      : (d.parents ?? []);
  const topLevel = typeof d.topLevel === "function" ? d.topLevel(c) : (d.topLevel ?? false);
  return {
    label: loaded ?? d.label ?? null,
    parents,
    ...(d.inlineTitle ? { inlineTitle: d.inlineTitle } : {}),
    topLevel,
    ...(d.levelKeys?.length ? { levelKeys: d.levelKeys } : {}),
  };
}

function noParents(): TrailLevel[] {
  return [];
}

/**
 * A call's level in the crumbs: "Call, Oct 23 3:15 PM", in the viewer's time zone. A call is
 * never named by its contact, so a contact opened from it reads ".../Call, Oct 23 3:15 PM/Victor
 * Vega".
 */
export function callLevelLabel(startedAt: string | null | undefined, timeZone?: string): string {
  const d = startedAt ? new Date(startedAt) : null;
  if (!d || Number.isNaN(d.getTime())) return "Call";
  const tz = timeZone ? { timeZone } : {};
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...tz });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...tz });
  return `Call, ${day} ${time}`;
}
